import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { platformSettingsQuery, updatePlatformSettingsMutation } from "@/api/queries";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { SelectField } from "@/components/ui/field";
import { Alert } from "@/components/ui/alert";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import type { IncidentMailChannel, UpdatePlatformSettingsRequest } from "@/contracts";
import { useApiClient } from "@/lib/api-context";

/**
 * Delivery switches for the whole deployment (ADR-103).
 *
 * The route is limited to a super admin. Turning a real channel on asks
 * first, because the next incident or invitation will actually leave.
 */
export function PlatformSettingsPage() {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const settings = useQuery(platformSettingsQuery(client));
  const [confirming, setConfirming] = useState(false);
  const loaded = settings.data;
  const loadedKey = loaded
    ? `${loaded.incidentChannel}:${loaded.whatsAppEnabled}:${loaded.transactionalEmailChannel}`
    : "";
  // Edits stay until the server snapshot changes. A new key drops them, so a
  // save that comes back from the API replaces the form without an effect.
  const [override, setOverride] = useState<{
    key: string;
    value: UpdatePlatformSettingsRequest;
  } | null>(null);
  const draft =
    loaded && override?.key === loadedKey
      ? override.value
      : loaded
        ? {
            incidentChannel: loaded.incidentChannel,
            whatsAppEnabled: loaded.whatsAppEnabled,
            transactionalEmailChannel: loaded.transactionalEmailChannel,
          }
        : null;

  function edit(next: UpdatePlatformSettingsRequest) {
    setOverride({ key: loadedKey, value: next });
  }

  const save = useMutation({
    ...updatePlatformSettingsMutation(client),
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: ["platform-settings"] });
    },
  });

  function submit(next: UpdatePlatformSettingsRequest) {
    if (loaded && turnsOnRealChannel(loaded, next)) {
      setConfirming(true);
      return;
    }

    save.mutate(next);
  }

  return (
    <>
      <PageHeader
        title={t("platformSettingsTitle")}
        description={t("platformSettingsDescription")}
      />

      {settings.isPending ? <LoadingBlock /> : null}
      {settings.isError ? (
        <ErrorBlock error={settings.error} onRetry={() => void settings.refetch()} />
      ) : null}

      {loaded && draft ? (
        <Card className="p-4">
          <form
            className="flex max-w-xl flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit(draft);
            }}
          >
            {loaded.emailAvailable ? null : (
              <p className="text-sm text-ink-muted">{t("platformSettingsEmailUnavailable")}</p>
            )}

            <SelectField
              label={t("platformSettingsIncidentChannel")}
              hint={t("platformSettingsIncidentHint")}
              value={draft.incidentChannel}
              data={channelOptions(
                t("platformSettingsConsole"),
                t("platformSettingsEmail"),
                loaded.emailAvailable,
              )}
              onChange={(value) => {
                if (value === "CONSOLE" || value === "EMAIL") {
                  edit({ ...draft, incidentChannel: value });
                }
              }}
            />

            <WhatsAppToggle
              label={t("platformSettingsWhatsApp")}
              hint={
                loaded.whatsAppAvailable
                  ? t("platformSettingsWhatsAppHint")
                  : t("platformSettingsWhatsAppUnavailable")
              }
              checked={draft.whatsAppEnabled}
              disabled={!loaded.whatsAppAvailable}
              onChange={(whatsAppEnabled) => edit({ ...draft, whatsAppEnabled })}
            />

            <SelectField
              label={t("platformSettingsTransactionalChannel")}
              hint={t("platformSettingsTransactionalHint")}
              value={draft.transactionalEmailChannel}
              data={channelOptions(
                t("platformSettingsConsole"),
                t("platformSettingsEmail"),
                loaded.emailAvailable,
              )}
              onChange={(value) => {
                if (value === "CONSOLE" || value === "EMAIL") {
                  edit({ ...draft, transactionalEmailChannel: value });
                }
              }}
            />

            {save.isError ? (
              <Alert>
                <p>{save.error.message}</p>
              </Alert>
            ) : null}

            <div>
              <Button type="submit" loading={save.isPending}>
                {t("common:save")}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {loaded && draft ? (
        <ConfirmDialog
          open={confirming}
          title={t("platformSettingsConfirmTitle")}
          confirmLabel={t("platformSettingsConfirm")}
          pending={save.isPending}
          onCancel={() => {
            if (!save.isPending) {
              setConfirming(false);
            }
          }}
          onConfirm={() => save.mutate(draft)}
        >
          <p>{t("platformSettingsConfirmBody")}</p>
          <ul className="mt-2 list-disc pl-5">
            {loaded.incidentChannel !== "EMAIL" && draft.incidentChannel === "EMAIL" ? (
              <li>{t("platformSettingsConfirmIncidents")}</li>
            ) : null}
            {!loaded.whatsAppEnabled && draft.whatsAppEnabled ? (
              <li>{t("platformSettingsConfirmWhatsApp")}</li>
            ) : null}
            {loaded.transactionalEmailChannel !== "EMAIL" &&
            draft.transactionalEmailChannel === "EMAIL" ? (
              <li>{t("platformSettingsConfirmTransactional")}</li>
            ) : null}
          </ul>
        </ConfirmDialog>
      ) : null}
    </>
  );
}

function turnsOnRealChannel(
  loaded: UpdatePlatformSettingsRequest,
  draft: UpdatePlatformSettingsRequest,
): boolean {
  return (
    (loaded.incidentChannel !== "EMAIL" && draft.incidentChannel === "EMAIL") ||
    (!loaded.whatsAppEnabled && draft.whatsAppEnabled) ||
    (loaded.transactionalEmailChannel !== "EMAIL" && draft.transactionalEmailChannel === "EMAIL")
  );
}

function channelOptions(
  consoleLabel: string,
  emailLabel: string,
  emailAvailable: boolean,
): { value: IncidentMailChannel; label: string; disabled?: boolean }[] {
  return [
    { value: "CONSOLE", label: consoleLabel },
    {
      value: "EMAIL",
      label: emailLabel,
      ...(emailAvailable ? {} : { disabled: true }),
    },
  ];
}

function WhatsAppToggle({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <input
        id="platform-whatsapp"
        type="checkbox"
        className="mt-1 size-4 rounded border-border-subtle"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor="platform-whatsapp" className="text-sm text-ink">
        <span className="font-medium">{label}</span>
        <span className="mt-0.5 block text-xs text-ink-muted">{hint}</span>
      </label>
    </div>
  );
}
